import { AIChatBoxMessagesInterceptor, AiEntityToken, link, linkButton, systemMessage, temporaryText } from '@aston/ai-chat';
import { TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { selectConfig } from '../../root-store/app-store/selectors';
import { AppConfiguration } from '../../app.configuration';
import * as R from '../../routes-definitions';

import { AiExportTriggerService } from './ai-export-trigger.service';

// Maps trigger-export AI tool catalog ids (Core.Middle.Services/Ai/TriggerExportAiToolHandler.cs)
// to the i18n key already used as that chart's own title, so the export button label matches the chart it exports.
const EXPORT_LABEL_KEYS: Record<string, string> = {
	ageingBalance: 'Home.Graph.AgeingBalance.Title',
	ageingBalanceDetailed: 'Home.Graph.AgeingBalance.Title',
	dsoMonitoring: 'SuperDebtors.Charts.DSO',
	averageOverdue: 'SuperDebtors.Charts.AverageDelay',
	averageDisputeResolution: 'SuperDebtors.Charts.AverageDisputeResolution.LTG',
	topOutstandingClients: 'Home.TopOutstanding.Title',
	topDebtorsWithClaims: 'SuperDebtors.Charts.DisputesFollowup.Title',
	estimatedCashing: 'SuperDebtors.Charts.EstimatedCashing',
	outstandingDunningLevel: 'SuperDebtors.Charts.Outstanding',
	totalRevenue: 'SuperDebtors.Charts.Turnover',
}

@Injectable({ providedIn: 'any' })
export class MiddleAIChatBoxMessagesInterceptor extends AIChatBoxMessagesInterceptor {
	i18n = inject(TranslateService)
	fatStore = inject(Store)
	exportTrigger = inject(AiExportTriggerService)

	config = toSignal(this.fatStore.select(selectConfig), { initialValue: {} as AppConfiguration })

	generateTemporaryButtons(text: string) {
		const buttons = []

		if (text.match(/DSO/)) {
			buttons.push(linkButton(this.i18n.instant('App.AIChat.Suggestions.GoToChartDSO'), R.getOverviewFullPath(R.DashboardsPageFinancialRoutePath)))
		}
		if (text.match(/Scoring/)) {
			buttons.push(linkButton(this.i18n.instant('App.AIChat.Suggestions.GoToChartRisks'), R.getOverviewFullPath(R.DashboardsPageOperationalRoutePath)))
		}
		if (text.match(/Retard/)) {
			buttons.push(linkButton(this.i18n.instant('App.AIChat.Suggestions.GoToChartDelays'), R.getOverviewFullPath(R.DashboardsPageFinancialRoutePath)))
		}
		if (text.match(/Action/)) {
			buttons.push(linkButton(this.i18n.instant('App.AIChat.Suggestions.GoToDunningActions'), R.getDunningActionsListRouteFullPath()))
		}
		if (text.match(/Group/)) {
			buttons.push(linkButton(this.i18n.instant('App.AIChat.Suggestions.GoToDunningGroups'), R.getDunningGroupsListRouteFullPath()))
		}
		if (text.match(/Sc.nario/)) {
			buttons.push(linkButton(this.i18n.instant('App.AIChat.Suggestions.GoToDunningScenarios'), R.getDunningScenariosListRouteFullPath()))
		}
		if (text.match(/Mod[eè]l/)) {
			buttons.push(linkButton(this.i18n.instant('App.AIChat.Suggestions.GoToDunningTemplates'), R.getDunningTemplatesListRouteFullPath()))
		}

		if (this.isFlakyResponse()) {
			const frb = this.buttonsFromFlakyResponse()

			if (frb.length) frb.forEach(b => buttons.push(b))
			else if (this.flakyResponsesCount() % 5 === 0) {
				const contactUs = this.i18n.instant('Support.Contact')
				const byZoho = this.i18n.instant('Support.TicketUrlText')
				buttons.push(linkButton(`${contactUs} ${byZoho}`, this.config().helpUrl))
			}
		}

		return systemMessage({ html: temporaryText(buttons.join('')) })
	}

	replaceWellKnownTokens(text: string) {
		const replaced = this.replaceSharedTokens(text)

		return this.replaceTextAndEntityTokens(replaced, value => value
			.replace(/【[^】]*】/g, '') // remove files references
			.replace(/08\s*90\s*21\s*38\s*39/g, `[08 90 21 38 39](tel:${this.i18n.instant('Support.Phone')})`)
			.replace(/\+33\s*8\s*90\s*21\s*38\s*39/g, `[08 90 21 38 39](tel:${this.i18n.instant('Support.Phone')})`)
			.replace(/support@astonai.com/g, `[${this.i18n.instant('Support.Email')}](mailto:${this.i18n.instant('Support.Email')})`)
			.replace(/ retard/i, ` ${link('retard', R.getOverviewFullPath(R.DashboardsPageFinancialRoutePath))}`)
			.replace(/ DSO /i, ` ${link('DSO', R.getOverviewFullPath(R.DashboardsPageFinancialRoutePath))} `)
			.replace(/ scoring/i, ` ${link('scoring', R.getOverviewFullPath(R.DashboardsPageOperationalRoutePath))})`)
			.replace(/ action /i, ` ${link('action', R.getDunningActionsListRouteFullPath())} `)
			.replace(/ groupe /i, ` ${link('groupe', R.getDunningGroupsListRouteFullPath())} `)
			.replace(/ scénario /i, ` ${link('scénario', R.getDunningScenariosListRouteFullPath())} `)
			.replace(/ modèle /i, ` ${link('modèle', R.getDunningTemplatesListRouteFullPath())} `))
	}

	protected replaceSharedTokens(text: string) {
		this.trackFlakyResponse(text)
		return text
	}

	protected override transformFullResponseText(text: string): string {
		return text
			.replace(/`\/([^`]*)`/g, (_, route: string) => link(route, route))
			.replace(/【[^】]*】/g, '')
			.replace(/08\s*90\s*21\s*38\s*39/g, `[08 90 21 38 39](tel:${this.i18n.instant('Support.Phone')})`)
			.replace(/\+33\s*8\s*90\s*21\s*38\s*39/g, `[08 90 21 38 39](tel:${this.i18n.instant('Support.Phone')})`)
			.replace(/support@astonai.com/g, `[${this.i18n.instant('Support.Email')}](mailto:${this.i18n.instant('Support.Email')})`)
			.replace(/ retard/i, ` ${link('retard', R.getOverviewFullPath(R.DashboardsPageFinancialRoutePath))}`)
			.replace(/ DSO /i, ` ${link('DSO', R.getOverviewFullPath(R.DashboardsPageFinancialRoutePath))} `)
			.replace(/ scoring/i, ` ${link('scoring', R.getOverviewFullPath(R.DashboardsPageOperationalRoutePath))})`)
			.replace(/ action /i, ` ${link('action', R.getDunningActionsListRouteFullPath())} `)
			.replace(/ groupe /i, ` ${link('groupe', R.getDunningGroupsListRouteFullPath())} `)
			.replace(/ scénario /i, ` ${link('scénario', R.getDunningScenariosListRouteFullPath())} `)
			.replace(/ modèle /i, ` ${link('modèle', R.getDunningTemplatesListRouteFullPath())} `)
	}

	protected entityBadge(entity: AiEntityToken) {
		const actions = []
		const route = this.getEntityRoute(entity)
		const rawType = this.escapeHtml(entity.type)
		const displayType = this.escapeHtml(this.getEntityType(entity.type))
		const id = this.escapeHtml(entity.id)
		const name = this.escapeHtml(entity.name)
		const routeAttribute = route ? ` data-entity-route="${this.escapeHtml(route)}"` : ''

		actions.push(route
			? `<button type="button" class="ai-entity-action" data-entity-action="open" data-entity-route="${this.escapeHtml(route)}" role="menuitem" style="display: block; width: 100%; border: 0; background: transparent; padding: 6px 12px; text-align: left; cursor: pointer; font: inherit; color: #333;">${this.i18n.instant('Actions.Open')}</button>`
			: '');


		if (entity.type.toLowerCase() === 'customer') {
			actions.push(`<button type="button" class="ai-entity-action" data-entity-action="open" data-entity-route="${this.escapeHtml(this.getEntityRoute({ ...entity, type: 'super_debtor_recommendations' }))}" role="menuitem" style="display: block; width: 100%; border: 0; background: transparent; padding: 6px 12px; text-align: left; cursor: pointer; font: inherit; color: #333;">${this.i18n.instant('SuperDebtors.Tabs.Recommendations')}</button>`);
		}

		actions.push(`
			<button type="button" class="ai-entity-action" data-entity-action="copy-id" role="menuitem" style="display: block; width: 100%; border: 0; background: transparent; padding: 6px 12px; text-align: left; cursor: pointer; font: inherit; color: #333;">${this.i18n.instant('Actions.Copy')}</button>`);

		return `<span class="ai-entity-token" data-entity-type="${rawType}" data-entity-id="${id}" data-entity-name="${name}"${routeAttribute} style="position: relative; display: inline-block;">
			<button type="button" class="ai-entity-badge" aria-haspopup="true" aria-expanded="false" style="display: inline-flex; align-items: center; gap: 0.35em; border: 1px solid #b8c7d9; border-radius: 999px; background: #eef5ff; color: #155aa7; padding: 0.05em 0.55em; font: inherit; cursor: pointer; line-height: 1.45;">
				<span style="font-weight: 600;">${name}</span>
				<span style="font-size: 0.75em; opacity: 0.7;">${displayType}</span>
				<span aria-hidden="true" style="font-size: 0.75em;">&#9662;</span>
			</button>
			<span class="ai-entity-menu" role="menu" style="display: none; position: absolute; z-index: 20; left: 0; top: calc(100% + 2px); min-width: 130px; padding: 4px 0; background: white; border: 1px solid #d5d5d5; border-radius: 4px; box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);">
				${actions.join('')}
			</span>
		</span>`
	}

	protected override getExportLabel(id: string): string {
		const key = EXPORT_LABEL_KEYS[id]
		if (!key) return super.getExportLabel(id)
		const translated = this.i18n.instant(key)
		return translated === key ? super.getExportLabel(id) : translated
	}

	protected override triggerExport(exportId: string, encodedFilters: string): void {
		this.exportTrigger.trigger(exportId, encodedFilters)
	}

	protected getEntityType(type: string): string {
		switch (type.toLowerCase()) {
		case 'customer':
		case 'super_debtor':
		case 'super-debtor':
			return this.i18n.instant('Bookmarks.SuperDebtor')
		case 'accounting':
		case 'accounting-document':
		case 'accounting_document':
			return this.i18n.instant('Bookmarks.AccountingDocument')
		case 'portfolio':
			return this.i18n.instant('Bookmarks.Portfolio')
		case 'action':
			return this.i18n.instant('Bookmarks.DunningAction')
		case 'scenario':
		case 'dunning-scenario':
		case 'dunningScenario':
			return this.i18n.instant('Dunning.Actions.Form.Scenario')
		case 'template':
		case 'dunning-template':
			return this.i18n.instant('Dunning.Actions.Form.Template')
		case 'group':
		case 'dunning-group':
			return this.i18n.instant('Forms.CommonLabels.DunningGroup')
		default:
			return type
		}
	}

	protected override getEntityRoute(entity: AiEntityToken): string | undefined {
		if (!/^\d+$/.test(entity.id)) return undefined
		const id = Number(entity.id)

		switch (entity.type.toLowerCase()) {
		case 'customer':
		case 'super_debtor':
			return R.getSuperDebtorDetailFullPath(id)
		case 'super_debtor_recommendations':
			return R.getSuperDebtorDetailFullPath(id, R.SuperDebtorDetailRecommendationsRoutePath)
		case 'accounting':
		case 'accounting_document':
		case 'accounting-document':
			return R.getAccountingDetailFullPath(id)
		case 'portfolio':
			return R.getSuperDebtorsListFullPath()
		case 'action':
		case 'dunning_action':
			return R.getDunningActionsListRouteFullPath() + `?id=${entity.id}`
		case 'scenario':
		case 'dunning-scenario':
			return R.getDunningScenarioEditRouteFullPath(id)
		case 'model':
		case 'template':
		case 'dunning-template':
			return R.getDunningTemplateEditRouteFullPath(id)
		case 'group':
		case 'dunning_group':
		case 'dunning-group':
			return R.getDunningGroupEditRouteFullPath(id)
		default:
			return undefined
		}
	}
}
